import { Col, Column, Container, ContainerSize, Heading, ListGroup, ListGroupItemColor, Row, Text } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";

type ListGroupProps = {};

const ListGroupPage = ({ }: ListGroupProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                List Group
            </Heading>

            <Text el="p">The <Text el="strong">ListGroup</Text> component is used to display a series of related content
                or items in a vertical
                list. Each item can be active, disabled, or interactive, making it ideal for menus, links, or
                selections.</Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Example
            </Heading>

            <Text className="mb-4">
                The simplest form of a <Text el="strong">list group</Text> is an unordered list containing list items
                with the appropriate
                classes.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Row>
                        <Column col={Col.Col3}>
                            <ListGroup>
                                <ListGroup.Item>An item</ListGroup.Item>
                                <ListGroup.Item>A second item</ListGroup.Item>
                                <ListGroup.Item>A third item</ListGroup.Item>
                                <ListGroup.Item>A fourth item</ListGroup.Item>
                                <ListGroup.Item>And a fifth one</ListGroup.Item>
                            </ListGroup>
                        </Column>
                    </Row>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ListGroupBasicCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Active item
            </Heading>

            <Text className="mb-4">
                You can highlight a specific list item by setting the <Text el="strong">active</Text> prop to true
                on <Text el="strong">ListGroupItem</Text>. This
                visually marks the item as the currently selected or active option.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Row>
                        <Column col={Col.Col3}>
                            <ListGroup>
                                <ListGroup.Item>An item</ListGroup.Item>
                                <ListGroup.Item active={true}>A second item</ListGroup.Item>
                                <ListGroup.Item>A third item</ListGroup.Item>
                                <ListGroup.Item>A fourth item</ListGroup.Item>
                                <ListGroup.Item>And a fifth one</ListGroup.Item>
                            </ListGroup>
                        </Column>
                    </Row>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ListGroupActiveItemCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Disable item
            </Heading>

            <Text className="mb-4">
                You can disable specific list items by setting the <Text el="strong">disabled prop</Text> to true
                on <Text el="strong">ListGroupItem</Text>. Disabled
                items appear visually faded and cannot be interacted with.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Row>
                        <Column col={Col.Col3}>
                            <ListGroup>
                                <ListGroup.Item>An item</ListGroup.Item>
                                <ListGroup.Item disabled={true}>A second item</ListGroup.Item>
                                <ListGroup.Item disabled={true}>A third item</ListGroup.Item>
                            </ListGroup>
                        </Column>
                    </Row>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ListGroupDisableItemCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Appearance
            </Heading>

            <Text className="mb-4">
                You can control how each <Text el="strong">ListGroupItem</Text> is rendered using the <Text el="strong">el
                    prop:</Text>
                <Text el="strong">Use el="a"</Text> along with <Text el="strong">href</Text> when you want the item to
                behave like a <Text el="strong">link</Text>.
                Use <Text el="strong">el="button"</Text> when the item should act like an interactive <Text
                    el="strong">button</Text>, ideal for triggering actions with <Text el="strong">onClick</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Row>
                        <Column col={Col.Col3}>
                            <ListGroup>
                                <ListGroup.Item el="a" href="#">An item</ListGroup.Item>
                                <ListGroup.Item el="a" href="#">A second item</ListGroup.Item>
                                <ListGroup.Item el="a" href="#">A third item</ListGroup.Item>
                            </ListGroup>
                        </Column>
                    </Row>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ListGroupLinkItemCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Row>
                        <Column col={Col.Col3}>
                            <ListGroup>
                                <ListGroup.Item el="button">An item</ListGroup.Item>
                                <ListGroup.Item el="button">A second item</ListGroup.Item>
                                <ListGroup.Item el="button">A third item</ListGroup.Item>
                            </ListGroup>
                        </Column>
                    </Row>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ListGroupButtonItemCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Color
            </Heading>

            <Text className="mb-4">
                You can apply different <Text el="strong">color</Text> styles to each <Text
                    el="strong">ListGroupItem</Text> using the <Text el="strong">color prop</Text>. This helps indicate
                importance, status, or category within a list.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Row>
                        <Column col={Col.Col3}>
                            <ListGroup>
                                <ListGroup.Item color={ListGroupItemColor.Primary}>Primary list group item</ListGroup.Item>
                                <ListGroup.Item color={ListGroupItemColor.Secondary}>Secondary list group item</ListGroup.Item>
                                <ListGroup.Item color={ListGroupItemColor.Info}>Info list group item</ListGroup.Item>
                                <ListGroup.Item color={ListGroupItemColor.Warning}>Warning list group item</ListGroup.Item>
                                <ListGroup.Item color={ListGroupItemColor.Danger}>Danger list group item</ListGroup.Item>
                                <ListGroup.Item color={ListGroupItemColor.Dark}>Dark list group item</ListGroup.Item>
                                <ListGroup.Item color={ListGroupItemColor.Light}>Light list group item</ListGroup.Item>
                            </ListGroup>
                        </Column>
                    </Row>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ListGroupItemColorCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
}
export default ListGroupPage;


const ListGroupBasicCode = `<Row>
    <Column col={Col.Col3}>
        <ListGroup>
            <ListGroup.Item>An item</ListGroup.Item>
            <ListGroup.Item>A second item</ListGroup.Item>
            <ListGroup.Item>A third item</ListGroup.Item>
            <ListGroup.Item>A fourth item</ListGroup.Item>
            <ListGroup.Item>And a fifth one</ListGroup.Item>
        </ListGroup>
    </Column>
</Row>`;

const ListGroupActiveItemCode = `<Row>
    <Column col={Col.Col3}>
        <ListGroup>
            <ListGroup.Item>An item</ListGroup.Item>
            <ListGroup.Item active={true}>A second item</ListGroup.Item>
            <ListGroup.Item>A third item</ListGroup.Item>
            <ListGroup.Item>A fourth item</ListGroup.Item>
            <ListGroup.Item>And a fifth one</ListGroup.Item>
        </ListGroup>
    </Column>
</Row>`;

const ListGroupDisableItemCode = `<Row>
    <Column col={Col.Col3}>
        <ListGroup>
            <ListGroup.Item>An item</ListGroup.Item>
            <ListGroup.Item disabled={true}>A second item</ListGroup.Item>
            <ListGroup.Item disabled={true}>A third item</ListGroup.Item>
        </ListGroup>
    </Column>
</Row>`;

const ListGroupLinkItemCode = `<Row>
    <Column col={Col.Col3}>
        <ListGroup>
            <ListGroup.Item el="a" href="#">An item</ListGroup.Item>
            <ListGroup.Item el="a" href="#">A second item</ListGroup.Item>
            <ListGroup.Item el="a" href="#">A third item</ListGroup.Item>
        </ListGroup>
    </Column>
</Row>`;

const ListGroupButtonItemCode = ` <Row>
    <Column col={Col.Col3}>
        <ListGroup>
            <ListGroup.Item el="button">An item</ListGroup.Item>
            <ListGroup.Item el="button">A second item</ListGroup.Item>
            <ListGroup.Item el="button">A third item</ListGroup.Item>
        </ListGroup>
    </Column>
</Row>`;

const ListGroupItemColorCode = `<Row>
    <Column col={Col.Col3}>
        <ListGroup el="ol">
            <ListGroup.Item color={ListGroupItemColor.Primary}>Primary list group item</ListGroup.Item>
            <ListGroup.Item color={ListGroupItemColor.Secondary}>Secondary list group item</ListGroup.Item>
            <ListGroup.Item color={ListGroupItemColor.Info}>Info list group item</ListGroup.Item>
            <ListGroup.Item color={ListGroupItemColor.Warning}>Warning list group item</ListGroup.Item>
            <ListGroup.Item color={ListGroupItemColor.Danger}>Danger list group item</ListGroup.Item>
            <ListGroup.Item color={ListGroupItemColor.Dark}>Dark list group item</ListGroup.Item>
            <ListGroup.Item color={ListGroupItemColor.Light}>Light list group item</ListGroup.Item>
        </ListGroup>
    </Column>
</Row>`;








